import { useEffect, useRef } from "react";
import type { WebSocketMessage } from "@vms/types";

/**
 * Singleton browser WS connection to the cloud's `/ws` endpoint.
 *
 * Mirrors the hub-ui pattern (`apps/hub-ui/src/api/ws.ts`) — one shared
 * connection across the SPA, listener set, exponential-backoff reconnect.
 * Two cloud-ui-specific differences:
 *
 *   1. Auth is via the existing session JWT (same Bearer token used for REST),
 *      passed as `?token=` because browsers can't set Authorization headers on
 *      the WebSocket constructor. Read from localStorage on each connect so
 *      we always use the freshest token after sign-in / refresh.
 *   2. Cloud-ui is multi-tenant — a single connection serves multiple venues
 *      depending on which venue page the operator is viewing. Pages call
 *      `subscribeToVenue(venueId)` to opt into broadcasts for that venue;
 *      the active set is replayed onto every new connection.
 */
type Listener = (msg: WebSocketMessage) => void;

const STORAGE_KEY = "vms-cloud-token";

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelayMs = 2_000;
const RECONNECT_MAX_MS = 60_000;
const listeners = new Set<Listener>();
const reconnectListeners = new Set<() => void>();
const activeSubscriptions = new Set<string>();

function readToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function connect(): void {
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }
  const token = readToken();
  if (!token) {
    // Not signed in yet — try again later. Login flow re-triggers connect()
    // by way of the listener registration on next page load.
    return;
  }
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const url = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
  const ws = new WebSocket(url);
  socket = ws;

  ws.onopen = () => {
    reconnectDelayMs = 2_000;
    // Replay every active subscription so a fresh socket is current with the
    // pages currently on screen.
    for (const venueId of activeSubscriptions) {
      try {
        ws.send(JSON.stringify({ type: "subscribe", venueId }));
      } catch {
        /* ignore */
      }
    }
    for (const listener of reconnectListeners) listener();
  };

  ws.onmessage = (event) => {
    let msg: WebSocketMessage;
    try {
      msg = JSON.parse(event.data) as WebSocketMessage;
    } catch {
      return;
    }
    for (const listener of listeners) listener(msg);
  };

  ws.onclose = () => {
    socket = null;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(connect, reconnectDelayMs);
    reconnectDelayMs = Math.min(reconnectDelayMs * 2, RECONNECT_MAX_MS);
  };

  ws.onerror = () => {
    // Browsers fire close right after — the close handler does the retry.
  };
}

/** Subscribe to incoming WebSocket messages. Auto-reconnects on drop. */
export function useCloudWebSocket(onMessage: (msg: WebSocketMessage) => void): void {
  const callbackRef = useRef(onMessage);
  callbackRef.current = onMessage;

  useEffect(() => {
    const listener: Listener = (msg) => callbackRef.current(msg);
    listeners.add(listener);
    connect();
    return () => {
      listeners.delete(listener);
    };
  }, []);
}

/** Run `refetch` whenever the cloud WebSocket (re)connects so a client that
 *  dropped catches up to the latest state without waiting for the next
 *  incremental message. */
export function useRefetchOnReconnect(refetch: () => void): void {
  const callbackRef = useRef(refetch);
  callbackRef.current = refetch;

  useEffect(() => {
    const listener = () => callbackRef.current();
    reconnectListeners.add(listener);
    return () => {
      reconnectListeners.delete(listener);
    };
  }, []);
}

/** Non-hook venue subscribe — returns an unsubscribe function. Useful when a
 *  caller needs to subscribe to a dynamic list of venues inside a single
 *  useEffect (rules of hooks forbid calling `useVenueSubscription` in a loop).
 *  Same coalescing behaviour as the hook. */
export function subscribeToVenue(venueId: string): () => void {
  activeSubscriptions.add(venueId);
  connect();
  if (socket && socket.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify({ type: "subscribe", venueId }));
    } catch {
      /* ignore */
    }
  }
  return () => {
    activeSubscriptions.delete(venueId);
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(JSON.stringify({ type: "unsubscribe", venueId }));
      } catch {
        /* ignore */
      }
    }
  };
}

/** Subscribe the shared connection to a venue's broadcast room while the
 *  caller component is mounted. Multiple subscribes are coalesced — the
 *  cloud server tracks per-socket sets, so re-subscribes are cheap. */
export function useVenueSubscription(venueId: string | null | undefined): void {
  useEffect(() => {
    if (!venueId) return;
    return subscribeToVenue(venueId);
  }, [venueId]);
}
